'use client';
import React, { useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { DecorativeWaves } from '@/components/ui/DecorativeWaves';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/atoms/carousel';
import HighlightedHeading from '@/components/molecules/HighlightedHeading';

interface Testimonial {
  id: string;
  name: string;
  text: string;
  image: string;
  formacao?: string;
  slug?: string;
  verified?: boolean;
}

interface TestimonialsCarouselProps {
  title?: string;
  subtitle?: string;
  description?: string;
  tag?: string;
  button?: {
    label: string;
    url: string;
    external_link?: boolean;
  };
  testimonials: Testimonial[];
  className?: string;
  onTestimonialClick?: (testimonialId: string) => void;
}

interface TestimonialCardProps {
  testimonial: Testimonial;
  isExpanded: boolean;
  onHover: () => void;
}

const DownloadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Círculo de fundo azul */}
    <circle cx="24" cy="24" r="24" fill="#377396" />
    {/* Seta branca */}
    <path d="M22.5858 32.1858C23.3668 32.9668 24.6332 32.9668 25.4142 32.1858L31.92 25.68C32.8478 24.7522 32.8478 23.2478 31.92 22.32C30.9922 21.3922 29.4878 21.3922 28.56 22.32L26.4 24.48V16.8C26.4 15.4745 25.3255 14.4 24 14.4C22.6745 14.4 21.6 15.4745 21.6 16.8V24.48L19.44 22.32C18.5122 21.3922 17.0078 21.3922 16.08 22.32C15.1522 23.2478 15.1522 24.7522 16.08 25.68L22.5858 32.1858Z" fill="white" />
  </svg>
);

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  testimonial,
  isExpanded,
  onHover
}) => {
  const handleClick = () => {
    if (testimonial.slug) {
      window.location.href = `/testemunhos/${testimonial.slug}`;
    }
  };

  return (
    <article
      onMouseEnter={onHover}
      className={`relative rounded-2xl md:rounded-3xl flex flex-col justify-end overflow-hidden cursor-pointer transition-all duration-500 ease-in-out h-full xl:h-auto min-h-[400px] sm:min-h-[450px] md:min-h-[500px] lg:min-h-[557px] ${isExpanded ? 'xl:flex-[2.5]' : 'xl:flex-1'
        }`}
    >
      {/* Imagem de fundo */}
      <div className="absolute inset-0">
        <Image
          src={testimonial.image}
          alt={testimonial.name}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          priority
        />
      </div>

      {/* Overlay gradiente - múltiplas camadas de escurecimento */}
      <div className="absolute inset-0 bg-[#414141]/20 rounded-2xl md:rounded-3xl" />
      <div className="absolute inset-0 bg-black/20 rounded-2xl md:rounded-3xl" />
      <div className="absolute inset-0 bg-black/20 rounded-2xl md:rounded-3xl" />

      {/* Conteúdo */}
      <div className="relative h-full flex flex-col justify-end px-4 sm:px-6 md:px-8 pb-6 sm:pb-8 md:pb-12">
        {/* Container com todo o conteúdo */}
        <div className="flex h-full flex-1 justify-end flex-col gap-4 md:gap-6 z-10">
          {/* Botão com ícone de download/seta para baixo */}
          <button
            type="button"
            onClick={handleClick}
            className="cursor-pointer w-fit hover:scale-110 transition-transform duration-300"
            aria-label={`Ver testemunho de ${testimonial.name}`}
          >
            <DownloadIcon />
          </button>

          {/* Nome */}
          <h3 className="text-stone-50 text-lg md:text-xl font-semibold font-poppins uppercase leading-tight">
            {testimonial.name}
          </h3>

          {/* Texto do testemunho - visível apenas quando expandido - aparece embaixo do nome */}
          <div className={`transition-all duration-500 overflow-hidden ${isExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="text-stone-50 text-sm md:text-base font-light font-poppins leading-5 md:leading-6 line-clamp-4" dangerouslySetInnerHTML={{ __html: testimonial.text }} />
          </div>
        </div>
      </div>
    </article>
  );
};

export const TestimonialsCarousel: React.FC<TestimonialsCarouselProps> = ({
  title = 'Experiências que iluminam o Futuro',
  //subtitle = 'Testemunhos e Casos de sucesso',
  description = 'Destacou como os nossos esforços em energia renovável têm impactado positivamente clientes e parceiros, promovendo um futuro sustentável.',
  tag,
  button,
  testimonials,
  className = '',
  //onTestimonialClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedCardIndex, setExpandedCardIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [carouselCurrentIndex, setCarouselCurrentIndex] = useState(0);
  const [carouselCount, setCarouselCount] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  // Carousel API handlers
  useEffect(() => {
    if (!carouselApi) return;

    setCarouselCount(carouselApi.scrollSnapList().length);
    setCarouselCurrentIndex(carouselApi.selectedScrollSnap());

    carouselApi.on('select', () => {
      setCarouselCurrentIndex(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const scrollToIndex = useCallback((index: number) => {
    carouselApi?.scrollTo(index);
  }, [carouselApi]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setExpandedCardIndex(0);
  };

  const visibleTestimonials = testimonials.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  return (
    <section className={`relative w-full bg-gray-50 py-12 md:py-16 lg:py-26 px-4 md:px-8 overflow-hidden ${className}`}>
      <div className="container mx-auto px-2 sm:px-4 md:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_3fr] gap-8 lg:gap-12 items-start z-10">
          {/* Left Content */}
          <div className="relative space-y-6 md:space-y-8 z-10">
            {/* Title */}
            <div className="max-w-sm">
              <HighlightedHeading text={title || ''} positions={[0]} />
            </div>

            {/* Tag Badge */}
            {tag && (
              <div className="inline-block bg-stone-300/20 rounded-[30px] px-4 md:px-6 py-2">
                <span className="text-gray-700 text-base md:text-lg font-normal font-poppins">
                  {tag}
                </span>
              </div>
            )}

            {/* Description */}
            <p className="text-gray-600 text-sm md:text-base font-light font-poppins leading-relaxed max-w-sm">
              {description}
            </p>

            {/* Button - Desktop */}
            {button && (
              <button
                type="button"
                onClick={() => {
                  if (button.external_link) {
                    window.open(button.url, '_blank');
                  } else {
                    window.location.href = button.url;
                  }
                }}
                className="hidden xl:flex bg-white relative z-20 cursor-pointer rounded-[30px] border-2 border-gray-400 px-6 md:px-8 py-2.5 md:py-3 items-center gap-2 hover:bg-gray-100 transition-colors group"
              >
                <span className="text-gray-700 text-sm md:text-base font-medium font-poppins">
                  {button.label}
                </span>
              </button>
            )}
          </div>

          {/* Right Content - Carousel */}
          <div className="relative">
            {/* Desktop: Grid com paginação (apenas xl+) */}
            <div className="hidden xl:block">
              <div className="flex gap-4 lg:gap-6 justify-center items-stretch">
                {visibleTestimonials.map((item, index) => (
                  <TestimonialCard
                    key={item.id}
                    testimonial={item}
                    isExpanded={expandedCardIndex === index}
                    onHover={() => setExpandedCardIndex(index)}
                  />
                ))}
              </div>
              {/* Indicadores de Paginação - Desktop */}
              {totalPages > 1 && (
                <div className="flex justify-start gap-3 mt-12">
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={`testimonials-pagination-${index}`}
                      onClick={() => goToSlide(index)}
                      className={`cursor-pointer rounded-full transition-all ${index === currentIndex
                        ? 'w-12 h-3 bg-[#377396]'
                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                        }`}
                      aria-label={`Ir para slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Mobile e Tablet: Carrossel */}
            <div className="xl:hidden">
              <Carousel className="w-full" setApi={setCarouselApi}>
                <CarouselContent className="-ml-2 md:-ml-4">
                  {testimonials.map((item) => (
                    <CarouselItem key={item.id} className="pl-2 md:pl-4 basis-[90%] sm:basis-[80%] md:basis-[65%] lg:basis-[50%] h-full">
                      <TestimonialCard
                        testimonial={item}
                        isExpanded={true}
                        onHover={() => { }}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
              {/* Pagination Dots - Mobile e Tablet */}
              {carouselCount > 1 && (
                <div className="flex justify-center gap-2 sm:gap-3 mt-6">
                  {Array.from({ length: carouselCount }).map((_, index) => (
                    <button
                      key={`testimonials-dot-${index}`}
                      onClick={() => scrollToIndex(index)}
                      className={`cursor-pointer rounded-full transition-all ${index === carouselCurrentIndex
                        ? 'w-10 sm:w-12 h-3 bg-[#377396]'
                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                        }`}
                      aria-label={`Ir para slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
              {/* Button - Mobile */}
              {button && (
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    onClick={() => {
                      if (button.external_link) {
                        window.open(button.url, '_blank');
                      } else {
                        window.location.href = button.url;
                      }
                    }}
                    className="bg-white relative z-20 cursor-pointer rounded-[30px] border-2 border-gray-400 px-6 md:px-8 py-2.5 md:py-3 flex items-center gap-2 hover:bg-gray-100 transition-colors group"
                  >
                    <span className="text-gray-700 text-sm md:text-base font-medium font-poppins">
                      {button.label}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ondas decorativas no fundo */}
      <div className="absolute bottom-16 left-0 right-0 pointer-events-none opacity-100 z-0">
        <DecorativeWaves />
      </div>
    </section>
  );
};

export type { Testimonial, TestimonialsCarouselProps };